import {
  Subscription,
  SubscriptionState,
  FiltersState,
  Filter,
  SubscriptionAccount
} from './store'

import { LOGOUT } from '../../../global/auth/store/reducers'
import { RequestState } from '../../types'

export const LOAD_SUBSCRIPTIONS_REQUEST = 'LOAD_SUBSCRIPTIONS_REQUEST'
export const LOAD_SUBSCRIPTIONS_RECEIVED = 'LOAD_SUBSCRIPTIONS_RECEIVED'
export const LOAD_SUBSCRIPTIONS_ERROR = 'LOAD_SUBSCRIPTIONS_ERROR'
export const LOAD_SUBSCRIPTION_DETAILS_REQUEST = 'LOAD_SUBSCRIPTION_DETAILS_REQUEST'
export const LOAD_SUBSCRIPTION_DETAILS_RECEIVED = 'LOAD_SUBSCRIPTION_DETAILS_RECEIVED'
export const LOAD_SUBSCRIPTION_DETAILS_ERROR = 'LOAD_SUBSCRIPTION_DETAILS_ERROR'
export const LOAD_SINGLE_SUBSCRIPTION_REQUEST = 'LOAD_SINGLE_SUBSCRIPTION_REQUEST'
export const LOAD_SINGLE_SUBSCRIPTION_RECEIVED = 'LOAD_SINGLE_SUBSCRIPTION_RECEIVED'
export const LOAD_SINGLE_SUBSCRIPTION_ERROR = 'LOAD_SINGLE_SUBSCRIPTION_ERROR'
export const SET_FILTER_PANEL_OPEN = 'SET_FILTER_PANEL_OPEN'
export const SET_SUBSCRIPTION_EXPANDED = 'SET_SUBSCRIPTION_EXPANDED'
export const LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_REQUEST = 'LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_REQUEST'
export const LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_RECEIVED = 'LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_RECEIVED'
export const LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_ERROR = 'LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_ERROR'

export type SubscriptionsAction =
  | { type: typeof LOAD_SUBSCRIPTIONS_REQUEST, page: number }
  | { type: typeof LOAD_SUBSCRIPTIONS_RECEIVED, subscriptions: Subscription[], totalPages: number, page: number, totalCount: number }
  | { type: typeof LOAD_SUBSCRIPTIONS_ERROR, error: Error }
  | { type: typeof LOAD_SUBSCRIPTION_DETAILS_REQUEST, subscriptionId: string }
  | { type: typeof LOAD_SUBSCRIPTION_DETAILS_RECEIVED, subscriptionId: string, accounts: SubscriptionAccount[] }
  | { type: typeof LOAD_SUBSCRIPTION_DETAILS_ERROR, subscriptionId: string }
  | { type: typeof SET_FILTER_PANEL_OPEN, filterPanelOpen: boolean }
  | { type: typeof LOAD_SINGLE_SUBSCRIPTION_REQUEST }
  | { type: typeof LOAD_SINGLE_SUBSCRIPTION_RECEIVED, subscription: Subscription, searchedForAccount?: SubscriptionAccount }
  | { type: typeof LOAD_SINGLE_SUBSCRIPTION_ERROR }
  | { type: typeof SET_SUBSCRIPTION_EXPANDED, subscriptionId: string, expanded: boolean }
  | { type: typeof LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_REQUEST, subscriptionId: string, accountId: string }
  | { type: typeof LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_RECEIVED, subscriptionId: string, accountId: string, filters: Filter[] }
  | { type: typeof LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_ERROR, subscriptionId: string, accountId: string, error: Error }

export function subscriptionsReducer(state: SubscriptionState, action: SubscriptionsAction): SubscriptionState {
  switch (action.type) {
    case LOAD_SUBSCRIPTIONS_REQUEST: {
      if (action.page === 1) {
        return {
          ...state,
          requestState: RequestState.Loading,
          subscriptions: [],
          loadedPages: 0,
          totalCount: 0,
          filterPanelOpen: false
        }
      } else {
        return {
          ...state,
          requestState: RequestState.Loading
        }
      }
    }

    case LOAD_SUBSCRIPTIONS_RECEIVED: {
      const newSubscriptions = action.page === 1 ? [...action.subscriptions] : state.subscriptions.concat(action.subscriptions)

      return {
        ...state,
        subscriptions: newSubscriptions,
        loadedPages: action.page,
        totalPages: action.totalPages,
        totalCount: action.totalCount,
        requestState: RequestState.Loaded
      }
    }

    case LOAD_SUBSCRIPTIONS_ERROR: {
      return {
        ...state,
        requestState: RequestState.Error
      }
    }

    case LOAD_SUBSCRIPTION_DETAILS_REQUEST: {
      return {
        ...state,
        subscriptions: state.subscriptions.map((subscription: Subscription) => {
          if (subscription.id === action.subscriptionId) {
            return {
              ...subscription,
              accounts: [],
              requestState: RequestState.Loading
            }
          } else {
            return subscription
          }
        })
      }
    }

    case LOAD_SUBSCRIPTION_DETAILS_RECEIVED: {
      return {
        ...state,
        subscriptions: state.subscriptions.map((subscription: Subscription) => {
          if (subscription.id === action.subscriptionId) {
            return {
              ...subscription,
              accounts: action.accounts,
              requestState: RequestState.Loaded
            }
          } else {
            return subscription
          }
        })
      }
    }

    case LOAD_SUBSCRIPTION_DETAILS_ERROR: {
      return {
        ...state,
        subscriptions: state.subscriptions.map((subscription: Subscription) => {
          if (subscription.id === action.subscriptionId) {
            return {
              ...subscription,
              requestState: RequestState.Error
            }
          } else {
            return subscription
          }
        })
      }
    }

    case SET_SUBSCRIPTION_EXPANDED: {
      return {
        ...state,
        subscriptions: state.subscriptions.map((subscription: Subscription) => {
          if (subscription.id === action.subscriptionId) {
            return {
              ...subscription,
              isExpanded: action.expanded
            }
          } else {
            return subscription
          }
        })
      }
    }

    case LOAD_SINGLE_SUBSCRIPTION_REQUEST: {
      return {
        ...state,
        requestState: RequestState.Loading,
        subscriptions: []
      }
    }

    case LOAD_SINGLE_SUBSCRIPTION_RECEIVED: {
      if (action.searchedForAccount) {
        return {
          ...state,
          requestState: RequestState.Loaded,
          subscriptions: [action.subscription],
          searchedForAccount: action.searchedForAccount
        }
      } else {
        return {
          ...state,
          requestState: RequestState.Loaded,
          subscriptions: [action.subscription]
        }
      }
    }

    case LOAD_SINGLE_SUBSCRIPTION_ERROR: {
      return {
        ...state,
        requestState: RequestState.Error,
        subscriptions: []
      }
    }

    case SET_FILTER_PANEL_OPEN: {
      return {
        ...state,
        filterPanelOpen: action.filterPanelOpen
      }
    }

    case LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_REQUEST: {
      return { 
        ...state,
        subscriptions: state.subscriptions.map((subscription: Subscription) => {
          if (subscription.id === action.subscriptionId) {
            return {
              ...subscription,
              accounts: subscription.accounts.map((subscriptionAccount: SubscriptionAccount) => {
                if (subscriptionAccount.id === action.accountId) {
                  return {
                    ...subscriptionAccount,
                    requestState: RequestState.Loading
                  }
                } else {
                  return subscriptionAccount
                }
              })
            }
          } else {
            return subscription
          }
        })
      }
    }

    case LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_RECEIVED: {
      return { 
        ...state,
        subscriptions: state.subscriptions.map((subscription: Subscription) => {
          if (subscription.id === action.subscriptionId) {
            return {
              ...subscription,
              accounts: subscription.accounts.map((subscriptionAccount: SubscriptionAccount) => {
                if (subscriptionAccount.id === action.accountId) {
                  return {
                    ...subscriptionAccount,
                    filters: action.filters,
                    requestState: RequestState.Loaded
                  }
                } else {
                  return subscriptionAccount
                }
              })
            }
          } else {
            return subscription
          }
        })
      }
    }

    case LOAD_SUBSCRIPTION_ACCOUNT_FILTERS_ERROR: {
      return { 
        ...state,
        subscriptions: state.subscriptions.map((subscription: Subscription) => {
          if (subscription.id === action.subscriptionId) {
            return {
              ...subscription,
              accounts: subscription.accounts.map((subscriptionAccount: SubscriptionAccount) => {
                if (subscriptionAccount.id === action.accountId) {
                  return {
                    ...subscriptionAccount,
                    requestState: RequestState.Loaded
                  }
                } else {
                  return subscriptionAccount
                }
              })
            }
          } else {
            return subscription
          }
        })
      }
    }

    default: {
      return state
    }
  }
}

export const LOAD_FILTERS_REQUEST = 'LOAD_FILTERS_REQUEST'
export const LOAD_FILTERS_RECEIVED = 'LOAD_FILTERS_RECEIVED'
export const LOAD_FILTERS_ERROR = 'LOAD_FILTERS_ERROR'

export type FiltersAction =
  | { type: typeof LOAD_FILTERS_REQUEST }
  | { type: typeof LOAD_FILTERS_RECEIVED, filters: Filter[] }
  | { type: typeof LOAD_FILTERS_ERROR }
  | { type: typeof LOGOUT }

export function filtersReducer(state: FiltersState, action: FiltersAction) {
  switch (action.type) {
    case LOAD_FILTERS_REQUEST: {
      return {
        ...state,
        filters: [],
        requestState: RequestState.Loading
      }
    }

    case LOAD_FILTERS_RECEIVED: {
      return {
        ...state,
        requestState: RequestState.Loaded,
        filters: action.filters
      }
    }

    case LOAD_FILTERS_ERROR: {
      return {
        ...state,
        requestState: RequestState.Error,
        filters: []
      }
    }

    case LOGOUT: {
      return {
        ...state,
        filters: [],
        requestState: RequestState.NotLoaded
      }
    }

    default: {
      return state
    }
  }
}
